import { useEffect } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { Close } from '@mui/icons-material';
import { CircularProgress, IconButton } from '@mui/material';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { useHistory, useLocation, useParams } from 'react-router';

import type { LatestImage } from '@/@types/latestImages';
import { usePreferences } from '@/react-query/usePreferences';

import { FmxIcon } from '../../../../../../../common-components/elements/VideaIcons/VideaIcons';
import routes from '../../../../../../../routes/routes.constants';
import type { CornerstoneImage } from '../../../../../../../utils/cornerstone/useCornerstone';
import { useCornerstone } from '../../../../../../../utils/cornerstone/useCornerstone';
import { useDicomCanvas } from '../../../../../../../utils/cornerstone/useDicomCanvas';
import { useIsEmbedded } from '../../../../../../../utils/hooks';
import annotatorConstants from '../../../../../annotator.constants';
import { imageFilterAtom, showPastVisitsAtom } from '../../../../../atoms';
import {
  activeImageIsSelectedAtom,
  casePresentationModeAtom,
} from '../../../../../casePresentation/atoms';
import type { ImageObject } from '../../../../../types';
import { imageBrowserExpandedAtom } from '../../atoms';

import * as Styled from './styles';

type ImageProps = {
  image: ImageObject | LatestImage;
  index: number;
  onClick?: (imageId: string) => void;
  disableUrlSelection?: boolean;
  section?: 'left' | 'right';
};

export const Image = ({
  image,
  index,
  onClick,
  disableUrlSelection = false,
  section,
}: ImageProps) => {
  const [dicomCanvasRef, setDicomCanvasRef] = useDicomCanvas();
  const { data: prefs } = usePreferences();
  const history = useHistory();
  const [currentImageFilter, setCurrentImageFilter] = useAtom(imageFilterAtom);
  const isCasePresentationMode = useAtomValue(casePresentationModeAtom);
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const isEmbedded = useIsEmbedded();

  const selectedPhotoId = params.get('selected-photo');
  const [isBrowserExpanded, setIsBrowserExpanded] = useAtom(
    imageBrowserExpandedAtom,
  );
  const showPastVisits = useAtomValue(showPastVisitsAtom);

  const isActiveImageSelected = useAtomValue(activeImageIsSelectedAtom);

  const isFocusOnActiveImage = !selectedPhotoId || isActiveImageSelected;

  const { id: selectedPatientId, imageId: activeImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();

  const isFmxImage = image?.imageGroup === 'FMX';
  const isActiveImage =
    !disableUrlSelection && image?.imageId === activeImageId;
  const isSelectedPhoto =
    !disableUrlSelection && image?.imageId === selectedPhotoId;

  const {
    attributes,
    listeners,
    setNodeRef,
    isOver: isDraggingOver,
    over: isDragging,
  } = useSortable({
    id: image.imageId,
    disabled:
      (image.detectedImageType === 'COLOR_IMAGE' && !isCasePresentationMode) ||
      showPastVisits,
  });
  const { isCornerstoneReady, loadImageFileIntoCanvas, imageLoadStatus } =
    useCornerstone({
      isThumbnail: true,
      dicomCanvasRef,
      activeImage: image as CornerstoneImage,
      viewportId:
        'image-bucket' + index + image.imageId + (section ? `-${section}` : ''),
      source:
        prefs?.useSidebarRedesign || !isBrowserExpanded
          ? 'image-browser'
          : 'image-browser-expanded',
    });

  useEffect(() => {
    if (isCornerstoneReady && image && dicomCanvasRef) {
      loadImageFileIntoCanvas(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'loadImageFileIntoCanvas'. Either include it or remove the dependency array.
  }, [image.imageId, isCornerstoneReady, dicomCanvasRef, index]);

  const handleClick = () => {
    if (onClick) {
      onClick(image.imageId);
    } else {
      if (!image?.imageId) {
        return;
      }

      if (image?.imageId === selectedPhotoId) {
        params.delete('selected-photo');
        history.replace({ search: params.toString() });
        return;
      }

      if (!isFocusOnActiveImage) {
        const queryParams = new URLSearchParams({
          'selected-photo': image?.imageId,
        });
        history.replace({ search: queryParams.toString() });
        return;
      }

      setIsBrowserExpanded(false);
      // Only reset filter if it's not already a valid view (FMX or BITEWING)
      if (
        currentImageFilter !== annotatorConstants.FMX &&
        currentImageFilter !== annotatorConstants.BITEWING
      ) {
        setCurrentImageFilter(annotatorConstants.FMX);
      }
      const newRoute = isEmbedded
        ? `/embedded${routes.ANNOTATOR}/${selectedPatientId}/${image.imageId}`
        : `${routes.ANNOTATOR}/${selectedPatientId}/${image.imageId}`;
      history.push({ pathname: newRoute, search: history.location.search });
    }
  };

  // there is no expanded when using the new design
  const expanded =
    prefs?.useSidebarRedesign || !isBrowserExpanded
      ? 'not-expanded'
      : 'expanded';

  return (
    <>
      {imageLoadStatus === 'loading' && (
        <Styled.LoadingSpinnerAdditionalImage>
          <CircularProgress />
        </Styled.LoadingSpinnerAdditionalImage>
      )}
      <Styled.ImageContainer
        data-testid="image-from-browser"
        ref={setNodeRef}
        onClick={handleClick}
        isDraggingOver={!isCasePresentationMode && isDraggingOver}
        isDragging={!isCasePresentationMode && !!isDragging}
        {...attributes}
        {...listeners}
        unselectable="on"
      >
        {imageLoadStatus === 'error' && <Styled.ErrorIcon />}

        <Styled.CornerstoneElement
          ref={setDicomCanvasRef}
          onContextMenu={(e) => e.preventDefault()}
          activeImage={isActiveImage}
          selectedPhoto={isSelectedPhoto}
          className={classNames(expanded, {
            'sidebar-redesign': prefs?.useSidebarRedesign,
          })}
        >
          {isFmxImage && (
            <div className="fmx">
              <FmxIcon />
            </div>
          )}
        </Styled.CornerstoneElement>
        {image?.imageId === selectedPhotoId && (
          <IconButton
            className="close"
            onClick={(e) => {
              e.stopPropagation();
              params.delete('selected-photo');
              history.replace({ search: params.toString() });
            }}
          >
            <Close className="close-icon" />
          </IconButton>
        )}
      </Styled.ImageContainer>
    </>
  );
};
